// ════════════════════════════════════════════════════════════════════
// Glass-i — Stage SALES-PROFORMA-LIST-UX-1 — Proforma List helpers (PURE).
//
// Pure, unit-testable ESM used by the Gmail-style Proforma List page. No
// axios/client, no React, no client-side full-dataset filtering — every
// filter/pagination decision is turned into SERVER query params so the list
// scales past a few hundred rows (the old page hard-fetched limit:200).
//
// The lifecycle rules (proformaStatusUi) are the single source of truth for
// which quick-actions a row exposes; this module only reshapes them for the
// list. Status colours/labels also come from proformaHelpers — never
// re-defined here.
// ════════════════════════════════════════════════════════════════════

import { statusColor, statusLabel, proformaStatusUi, formatMoney } from './proformaHelpers.js';

// ── Tabs ────────────────────────────────────────────────────────────
// The lifecycle list tabs. `status` is the value sent to the server;
// `countKey` reads the matching field from statusCounts(). (The "All" tab was
// removed — the four lifecycle states cover every proforma.) buildListParams
// still treats an explicit ALL as "no status filter" for safety.
export const PROFORMA_TABS = [
  { key: 'PENDING',   status: 'PENDING',   label: 'Pending',   countKey: 'PENDING' },
  { key: 'CONFIRMED', status: 'CONFIRMED', label: 'Confirmed', countKey: 'CONFIRMED' },
  { key: 'HOLD',      status: 'HOLD',      label: 'Hold',      countKey: 'HOLD' },
  { key: 'CANCELLED', status: 'CANCELLED', label: 'Cancelled', countKey: 'CANCELLED' }
];

// Tabs that raise an "attention dot" when their count is > 0 — the two states
// that need operator action (something awaiting response / paused).
export const ATTENTION_TABS = new Set(['PENDING', 'HOLD']);

export function isValidTabKey(key) {
  return PROFORMA_TABS.some((t) => t.key === key);
}

// Debounce window for the server-side search box (ms). Exported so the page and
// the test agree on one value.
export const SEARCH_DEBOUNCE_MS = 300;

export const PAGE_SIZE_OPTIONS = [25, 50, 100];
export const DEFAULT_PAGE_SIZE = 50;

function toInt(v, fallback) {
  const n = Math.trunc(Number(v));
  return Number.isFinite(n) ? n : fallback;
}

// ── Server query params ─────────────────────────────────────────────
// buildListParams — turn UI state into the exact params the backend list()
// expects. Pagination is OFFSET based (page/pageSize → limit/offset). `status`
// is OMITTED when the All tab is active (server treats absent status as ALL);
// blank search/dates are omitted too so the URL/query stays clean.
export function buildListParams({ status, search, page, pageSize, dateFrom, dateTo, salespersonEmployeeId } = {}) {
  const size = Math.max(1, Math.min(100, toInt(pageSize, DEFAULT_PAGE_SIZE)));
  const pg = Math.max(1, toInt(page, 1));
  const params = { limit: size, offset: (pg - 1) * size };
  const st = status == null ? '' : String(status).trim().toUpperCase();
  // ALL (or blank) ⇒ no status filter. Everything else is passed verbatim.
  if (st && st !== 'ALL') params.status = st;
  const q = search == null ? '' : String(search).trim();
  if (q) params.search = q;
  if (dateFrom) params.dateFrom = String(dateFrom);
  if (dateTo) params.dateTo = String(dateTo);
  if (salespersonEmployeeId) params.salespersonEmployeeId = String(salespersonEmployeeId);
  return params;
}

// Counts params — the same CONTEXT (search/date/salesperson) minus status +
// pagination, so each tab's badge matches the rows that tab would show.
export function buildCountsParams({ search, dateFrom, dateTo, salespersonEmployeeId } = {}) {
  const params = {};
  const q = search == null ? '' : String(search).trim();
  if (q) params.search = q;
  if (dateFrom) params.dateFrom = String(dateFrom);
  if (dateTo) params.dateTo = String(dateTo);
  if (salespersonEmployeeId) params.salespersonEmployeeId = String(salespersonEmployeeId);
  return params;
}

// ── Pagination label ────────────────────────────────────────────────
// pageInfo — the "1–50 of 248" range string + numeric bounds. Clamps a page
// that overshoots the total and yields an empty ("0 of 0") label when there
// are no rows.
export function pageInfo(total, page, pageSize) {
  const t = Math.max(0, toInt(total, 0));
  const size = Math.max(1, toInt(pageSize, DEFAULT_PAGE_SIZE));
  if (t === 0) return { from: 0, to: 0, page: 1, pageCount: 0, total: 0, label: '0 of 0' };
  const pageCount = Math.max(1, Math.ceil(t / size));
  const pg = Math.min(Math.max(1, toInt(page, 1)), pageCount);
  const from = (pg - 1) * size + 1;
  const to = Math.min(t, pg * size);
  // en-dash between the bounds (Gmail-style "1–50 of 248").
  return { from, to, page: pg, pageCount, total: t, label: `${from}–${to} of ${t}` };
}

// ── Row projection ──────────────────────────────────────────────────
// Primary line (bold): proforma no · customer name. Secondary line: salesperson
// · city (state) · GSTIN. Both degrade gracefully when a field is missing.
function trimOr(v, fallback) {
  const s = v == null ? '' : String(v).trim();
  return s || fallback;
}

export function rowCustomerName(row) {
  const b = (row && row.billToSnapshot) || {};
  return trimOr(b.name || b.displayName, '—');
}

// City (State) — either alone shows solo; both blank ⇒ empty string.
export function rowLocation(row) {
  const b = (row && row.billToSnapshot) || {};
  const city = trimOr(b.city, '');
  const state = trimOr(b.state || b.gstStateName, '');
  if (city && state) return `${city} (${state})`;
  return city || state || '';
}

export function rowGstin(row) {
  const b = (row && row.billToSnapshot) || {};
  return trimOr(b.gstin, '');
}

export function rowSalesperson(row) {
  return trimOr(row && row.salespersonName, '');
}

export function rowProformaNo(row) {
  return trimOr(row && row.proformaNo, (row && row.id) || '—');
}

// Monogram initials for the customer avatar. Two words → their initials
// ("Velachery Glass" → "VG"); one word → its first two letters ("Google" →
// "GO"); empty → "#". Brackets/punctuation are stripped first.
export function customerInitials(name) {
  const s = String(name == null ? '' : name).replace(/[^\p{L}\p{N}\s]/gu, ' ').trim();
  if (!s) return '#';
  const words = s.split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return words[0].slice(0, 2).toUpperCase();
}

export function rowAmount(row) {
  return formatMoney(row && row.grandTotal);
}

// Prefer the business proforma date; fall back to the audit updated stamp.
export function rowDate(row) {
  const d = (row && (row.proformaDate || row.updatedAt || row.createdAt)) || null;
  if (!d) return '';
  const s = String(d);
  // ISO timestamps → date part only; plain YYYY-MM-DD passes through.
  return s.length >= 10 ? s.slice(0, 10) : s;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// Display date for the ledger row: "02 Jul 2026" from a YYYY-MM-DD(…) string.
// Pure + locale-free (no Date parsing) so it renders identically everywhere;
// anything that isn't a YYYY-MM-DD head passes straight through.
export function formatListDate(value) {
  const s = value == null ? '' : String(value);
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return s;
  const mon = MONTHS[Number(m[2]) - 1];
  return mon ? `${m[3]} ${mon} ${m[1]}` : s;
}

// Convenience: the row's display date already formatted.
export function rowDateLabel(row) {
  return formatListDate(rowDate(row));
}

function toNum(v) { const n = Number(v); return Number.isFinite(n) ? n : 0; }

// ── Extra list fields (Stage list-ux field expansion) ───────────────
// Bill-to mobile (from the customer snapshot).
export function rowMobile(row) {
  return trimOr((row && row.billToSnapshot) && row.billToSnapshot.mobile, '');
}
// Charge area in SQUARE METRES — "2.57 sqm" (2 dp) or '' when absent.
export function rowChargeSqmLabel(row) {
  const v = toNum(row && row.totalChargeSqm);
  if (!(v > 0)) return '';
  return `${v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} sqm`;
}
// Piece count — "1 pc" / "3 pcs" or '' when absent.
export function rowQtyLabel(row) {
  return rowSizeMetric(row).pieces;
}
// Job type (workType). Shown as stored; '' when unset.
export function rowJobType(row) {
  return trimOr(row && row.workType, '');
}
// Preparer's username (who created the proforma). '' when unresolved.
export function rowPreparedBy(row) {
  return trimOr(row && row.preparedByName, '');
}
// Confirmed-on display date — only for confirmed proformas; '' otherwise.
export function rowConfirmedOnLabel(row) {
  return formatListDate(rowDateOnly(row && row.confirmedAt));
}
// Proforma date, display-formatted ("02 Jul 2026").
export function rowPfDateLabel(row) {
  return formatListDate(row && row.proformaDate);
}
// WO-3.x — the released Work Order number + its release date (the WO's created_at
// head), shown alongside the proforma no/date. Blank when the order has not been
// released to production (no live Work Order).
export function rowWoNo(row) {
  return trimOr(row && row.woNo, '');
}
export function rowWoDateLabel(row) {
  return formatListDate(rowDateOnly(row && row.woDate));
}
// Trim an ISO timestamp to its date head (shared by confirmedOn).
function rowDateOnly(d) {
  if (!d) return '';
  const s = String(d);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

// The job's SIZE — the fact a glass fabricator scans for after the amount:
// total charge area (sqft) and piece count. Returns '' for a part that is
// zero/absent so the row can hide it. area → 1 dp; qty → integer when whole.
export function rowSizeMetric(row) {
  const sqft = toNum(row && row.totalChargeSqft);
  const qty = toNum(row && row.totalQty);
  const area = sqft > 0 ? `${(Math.round(sqft * 10) / 10).toLocaleString('en-IN')} sqft` : '';
  let pieces = '';
  if (qty > 0) {
    const whole = Math.abs(qty - Math.round(qty)) < 1e-6;
    const n = whole ? Math.round(qty) : Math.round(qty * 100) / 100;
    pieces = `${n} ${whole && n === 1 ? 'pc' : 'pcs'}`;
  }
  return { area, pieces };
}

// The bold + muted two-line summary in one call (handy for tests + the row).
export function rowPrimary(row) {
  return { proformaNo: rowProformaNo(row), customer: rowCustomerName(row) };
}
export function rowSecondary(row) {
  return { salesperson: rowSalesperson(row), location: rowLocation(row), gstin: rowGstin(row) };
}

// ── Row badge (status tag) ──────────────────────────────────────────
// Text + colour (never colour-only — the label carries the meaning for
// colour-blind users + screen readers).
export function rowBadge(row) {
  const status = row && row.status;
  return { color: statusColor(status), label: statusLabel(status), status: status || null };
}

// ── Derived invoice badge (Phase 2, read-only) ──────────────────────
// Sourced from the backend-derived row.invoiceState (NOT_INVOICED / DRAFT_INVOICE
// / POSTED). This is DISTINCT from the proforma lifecycle status above — it says
// whether a Sales Invoice exists for this order, not the proforma's own state.
export function rowInvoiceBadge(row) {
  const state = String((row && row.invoiceState) || 'NOT_INVOICED').toUpperCase();
  const map = {
    NOT_INVOICED: { color: 'default', label: 'Not Invoiced' },
    DRAFT_INVOICE: { color: 'gold', label: 'Draft Invoice' },
    POSTED: { color: 'green', label: 'Posted' }
  };
  const badge = map[state] || map.NOT_INVOICED;
  return { ...badge, state, invoiceId: (row && row.invoiceId) || null, invoiceNo: (row && row.invoiceNo) || null };
}

// ── Quick actions ───────────────────────────────────────────────────
// rowActions — derive the allowed hover/⋯ quick-actions for a row STRICTLY
// from proformaStatusUi (the lifecycle single source of truth). `open`/`print`
// are always available (read-only views can still open + print); the lifecycle
// transitions are gated. `needsReason` marks the ones that require a modal.
export function rowActions(row) {
  const ui = proformaStatusUi(row && row.status);
  const actions = [];
  if (ui.canConfirm) actions.push({ key: 'confirm', label: 'Confirm', needsReason: false });
  if (ui.canHold)    actions.push({ key: 'hold', label: 'Hold', needsReason: true });
  if (ui.canReopen)  actions.push({ key: 'reopen', label: 'Move to Pending', needsReason: false });
  if (ui.canCancel)  actions.push({ key: 'cancel', label: 'Cancel', needsReason: true });
  return { open: true, print: true, readOnly: ui.readOnly === true, transitions: actions };
}

export { statusColor, statusLabel, formatMoney };
